'use client';

import type { ReactNode } from 'react';
import { styled } from 'styled-components';
import * as Styled from '@lib/styles';
import type { CtaProps } from '@types';
import Button from '../components/Button';
import AccordionList from '../components/AccordionList';

type FAQProps = {
  /**
   * What the H3 element says
   */
  headline: string;

  /**
   * Descriptive paragraph
   */
  description: string;

  /**
   * Accordion components passed in as children
   */
  children: ReactNode[];

  /**
   * Optional primary button props
   */
  primaryBtn?: CtaProps | null;

  /**
   * Optional secondary button props
   */
  secondaryBtn?: CtaProps | null;
};

const FAQContainer = styled.div<{ $hasCtas: boolean }>`
  display: grid;
  padding: ${({ theme }) => `${theme.spacing[3]} 0`};

  .FAQHeader {
    div {
      display: flex;
      flex-direction: column;
    }
  }
  ${({ theme }) => theme.mq.minWidth.medium} {
    ${({ theme }) => theme.grid.columns.medium}
    .FAQHeader {
      grid-column: 1 / 4;
    }
    .FAQAccordionList {
      grid-column: 1 / -1;
    }
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    ${({ theme }) => theme.grid.columns.large}
    padding: ${({ theme }) => `${theme.spacing[4]} 0`};

    .FAQHeader {
      grid-column: ${({ $hasCtas }) => ($hasCtas ? '1 / 5' : '1 / 4')};
    }

    .FAQAccordionList {
      grid-column: ${({ $hasCtas }) => ($hasCtas ? '5 / 13' : '4 / 13')};
    }
  }
`;

const FAQHeadline = styled.h3`
  margin-bottom: ${({ theme }) => theme.spacing[1]};
`;
const FAQDescription = styled(Styled.BodySm)`
  margin-bottom: ${({ theme }) => theme.spacing[1]};
`;

const FAQButtons = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[1]};
  a,
  span {
    width: 100%;
    text-align: center;
    /* Override the default font size for the buttons on all widths */
    font-size: 18px !important;
  }
`;

const FAQ = ({ headline, description, children, primaryBtn = null, secondaryBtn = null }: FAQProps) => {
  const hasCtas = Boolean(primaryBtn || secondaryBtn);

  return (
    <FAQContainer $hasCtas={hasCtas}>
      <div className="FAQHeader">
        <FAQHeadline>{headline}</FAQHeadline>
        <FAQDescription>{description}</FAQDescription>
        {hasCtas && (
          <FAQButtons>
            {primaryBtn && (
              <Button
                href={primaryBtn.href}
                size="mini"
                trackingLabel={primaryBtn.trackingLabel}
                trackingProperty={primaryBtn.trackingProperty}
              >
                {primaryBtn.text}
              </Button>
            )}
            {secondaryBtn && (
              <Button
                color="blue700"
                href={secondaryBtn.href}
                size="mini"
                trackingLabel={secondaryBtn.trackingLabel}
                trackingProperty={secondaryBtn.trackingProperty}
                variant="secondary"
              >
                {secondaryBtn.text}
              </Button>
            )}
          </FAQButtons>
        )}
      </div>

      <AccordionList className="FAQAccordionList">{children}</AccordionList>
    </FAQContainer>
  );
};

export default FAQ;
